import { Box, CircularProgress, Button } from '@mui/material'
import { addDays, formatISO, parseISO } from 'date-fns'
import { Formik } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import Modal from 'components/Modal/Modal'
import theme from 'lib/theme'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { SlotDetails } from 'types/model/delivery'
import { SlotSchema } from 'lib/utils'
import { ValueProps as NewSlotValueProps } from './NewSlotModal'
import { SlotContent } from './SlotContent'

export type ValueProps = {
  date: Date
  startTime: Date
  endTime: Date
  orderQuota: string
  graceHours: string
  feeProducts: string
  deliveryFee: string
  visibilityStart: Date | null
  visibilityEnd: Date | null
  active: boolean
  recurrent: boolean
  validityStart: Date | null
  validityEnd: Date | null
  attributes: boolean
}

type EditSlotModalProps = {
  isFetching: boolean
  slotDetails: SlotDetails
  isLoading: boolean
  isLoadingDuplicate: boolean
  handleSubmit: (values: ValueProps) => Promise<void>
  handleDuplicate: (values: NewSlotValueProps) => Promise<void>
  modal: { right: boolean }
  handleCloseModal: () => void
  deliverySlot?: boolean
}

const getRecurringDateTime = (number: string, date: Date, endTime: string) => {
  const recurringDate = addDays(date, Number(number))
  const dateString = `${formatISO(recurringDate, {
    representation: 'date',
  })}T${endTime}`
  return new Date(dateString)
}

export const EditSlotModal = ({
  isFetching,
  slotDetails,
  handleCloseModal,
  isLoading,
  isLoadingDuplicate,
  handleSubmit,
  modal,
  handleDuplicate,
  deliverySlot,
}: EditSlotModalProps) => {
  const { t } = useTranslation()
  const [submitAction, setSubmitAction] = useState<'SAVE' | 'DUPLICATE' | null>(
    null
  )
  const { dateData } = useServiceAreas()

  const initialValues = {
    date:
      slotDetails.date && slotDetails.date.length > 1
        ? parseISO(slotDetails.date)
        : getRecurringDateTime(
            slotDetails.date,
            dateData.startOfWeek,
            slotDetails.startTime
          ),
    startTime:
      slotDetails.date && slotDetails.date.length > 1
        ? parseISO(`${slotDetails.date}T${slotDetails.startTime}`)
        : getRecurringDateTime(
            slotDetails.date,
            dateData.startOfWeek,
            slotDetails.startTime
          ),
    endTime:
      slotDetails.date && slotDetails.date.length > 1
        ? parseISO(`${slotDetails.date}T${slotDetails.endTime}`)
        : getRecurringDateTime(
            slotDetails.date,
            dateData.startOfWeek,
            slotDetails.endTime
          ),
    orderQuota: slotDetails.orderQuota ? slotDetails.orderQuota.toString() : '',
    graceHours: slotDetails.graceHours ? slotDetails.graceHours.toString() : '',
    feeProducts: slotDetails?.product?.masterProductId?.toString() || '',
    deliveryFee:
      slotDetails.deliveryPrice || Number(slotDetails.deliveryPrice) === 0
        ? slotDetails.deliveryPrice
        : '',
    visibilityStart: slotDetails.visibilityStart
      ? parseISO(slotDetails.visibilityStart)
      : null,
    visibilityEnd: slotDetails.visibilityEnd
      ? parseISO(slotDetails.visibilityEnd)
      : null,
    active: slotDetails.active || false,
    recurrent: slotDetails.type === 'Recurrent' || false,
    validityStart: slotDetails.validityStart
      ? parseISO(slotDetails.validityStart)
      : null,
    validityEnd: slotDetails.validityEnd
      ? parseISO(slotDetails.validityEnd)
      : null,
    attributes: Boolean(
      deliverySlot && slotDetails.attributes?.includes('ASAP')
    ),
  }

  const handleSubmitForm = (values: ValueProps) => {
    if (submitAction === 'SAVE') {
      handleSubmit(values)
    }
    if (submitAction === 'DUPLICATE') {
      handleDuplicate(values)
    }
  }

  if (isFetching) {
    return <LoadingIndicator />
  }
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SlotSchema}
      onSubmit={async (values) => handleSubmitForm(values)}>
      {({ isValid, dirty, handleSubmit }) => (
        <Modal
          setModal={handleCloseModal}
          modal={modal}
          showButton={false}
          title={t('serviceArea.editSlot')}>
          <SlotContent deliverySlot={deliverySlot}>
            <Box sx={{ position: 'fixed', bottom: theme.spacing(2) }}>
              <Box display='flex' width='440px' justifyContent='space-between'>
                <Button
                  disabled={!isValid || !dirty}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.commonColors.white,
                    height: theme.spacing(4),
                    width: '48%',
                  }}
                  onClick={() => {
                    setSubmitAction('SAVE')
                    handleSubmit()
                  }}>
                  {isLoading ? (
                    <CircularProgress
                      size='20px'
                      sx={{
                        color: theme.palette.commonColors.white,
                      }}
                    />
                  ) : (
                    t('saveChanges')
                  )}
                </Button>
                <Button
                  disabled={!isValid}
                  onClick={() => {
                    setSubmitAction('DUPLICATE')
                    handleSubmit()
                  }}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.commonColors.white,
                    height: theme.spacing(4),
                    width: '48%',
                  }}>
                  {isLoadingDuplicate ? (
                    <CircularProgress
                      size='20px'
                      sx={{
                        color: theme.palette.commonColors.white,
                      }}
                    />
                  ) : (
                    t('duplicate')
                  )}
                </Button>
              </Box>
            </Box>
          </SlotContent>
        </Modal>
      )}
    </Formik>
  )
}
